import type { EditorSignerRole } from "@/lib/sign/templateEditorModel";
import type { SignChannel } from "@/lib/sign/signTemplateApiClient";

type Props = {
  signers: EditorSignerRole[];
  readOnly?: boolean;
  onChange: (signers: EditorSignerRole[]) => void;
};

const CHANNELS: SignChannel[] = ["email", "sms", "embedded"];

export function SignTemplateSignersPanel({ signers, readOnly, onChange }: Props) {
  function update(index: number, patch: Partial<EditorSignerRole>) {
    const next = signers.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(next);
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= signers.length) return;
    const next = [...signers];
    const tmp = next[index]!;
    next[index] = next[target]!;
    next[target] = tmp;
    onChange(next.map((s, i) => ({ ...s, order: i })));
  }

  function remove(index: number) {
    onChange(
      signers
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, order: i }))
    );
  }

  function addRole() {
    const role = `signer_${signers.length + 1}`;
    onChange([...signers, { role, label: role, order: signers.length, defaultChannel: "email" }]);
  }

  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Signer roles</h4>
        {!readOnly ? (
          <button
            type="button"
            onClick={addRole}
            className="rounded border border-gray-300 px-2 py-0.5 text-xs dark:border-zinc-600"
          >
            Add role
          </button>
        ) : null}
      </div>
      <ul className="space-y-2">
        {signers.map((signer, index) => (
          <li key={`${signer.role}-${index}`} className="grid grid-cols-[1fr_auto] gap-2 rounded border border-gray-100 p-2 dark:border-zinc-800">
            <div className="grid gap-1.5 sm:grid-cols-2">
              <label className="text-[10px] text-gray-500">
                Role key
                <input
                  disabled={readOnly}
                  value={signer.role}
                  onChange={(e) => update(index, { role: e.target.value.trim() })}
                  className="mt-0.5 w-full rounded border border-gray-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
                />
              </label>
              <label className="text-[10px] text-gray-500">
                Label
                <input
                  disabled={readOnly}
                  value={signer.label ?? ""}
                  onChange={(e) => update(index, { label: e.target.value })}
                  className="mt-0.5 w-full rounded border border-gray-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
                />
              </label>
              <label className="text-[10px] text-gray-500 sm:col-span-2">
                Default channel
                <select
                  disabled={readOnly}
                  value={signer.defaultChannel ?? "email"}
                  onChange={(e) => update(index, { defaultChannel: e.target.value as SignChannel })}
                  className="mt-0.5 w-full rounded border border-gray-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!readOnly ? (
              <div className="flex flex-col gap-1">
                <button type="button" className="text-xs" onClick={() => move(index, -1)} disabled={index === 0}>
                  ↑
                </button>
                <button
                  type="button"
                  className="text-xs"
                  onClick={() => move(index, 1)}
                  disabled={index === signers.length - 1}
                >
                  ↓
                </button>
                <button type="button" className="text-xs text-red-600" onClick={() => remove(index)}>
                  ✕
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
